import { type MapRef, LayerProps } from 'react-map-gl-v7';
import { discoverColors } from '@discover-styles/colors';
import { FILTER_NOT_CLUSTERS, RESTAURANT_LAYER_ID } from './consts';

// Common filter expressions
const FILTER_IS_SELECTED = ['get', 'isSelected'];
const FILTER_HAS_IMAGE = ['==', ['get', 'hasImage'], true];
const FILTER_OFFERS_FIRST_TABLE = ['get', 'offersFirstTable'];
const FILTER_GET_RATING: mapboxgl.Expression = ['get', 'rating'];
const FILTER_GET_TITLE = ['get', 'title'];

// Filter for valid rating (greater than 0)
const FILTER_HAS_VALID_RATING: mapboxgl.Expression = ['>', FILTER_GET_RATING, 0];

// Formatted rating with 1 decimal place
const FILTER_GET_FORMATTED_RATING: mapboxgl.Expression = [
  'case',
  FILTER_HAS_VALID_RATING,
  [
    'case',
    ['==', ['round', FILTER_GET_RATING], FILTER_GET_RATING],
    ['concat', ['to-string', FILTER_GET_RATING], '.0'],
    ['to-string', ['/', ['round', ['*', FILTER_GET_RATING, 10]], 10]],
  ],
  'New',
];

// Name truncation constants
const MAX_NAME_LENGTH = 10;

// Common style constants
const COMMON_TEXT_FONT = ['DIN Pro Medium', 'Arial Unicode MS Bold'];

// Base pin layer configuration factory
const createPinLayerConfig = (
  id: string,
  filter: mapboxgl.SymbolLayer['filter'],
  iconImageProperty: string,
  textField: mapboxgl.SymbolLayout['text-field']
): LayerProps => ({
  id,
  type: 'symbol',
  filter,
  layout: {
    'symbol-z-order': 'viewport-y',
    'icon-image': ['get', iconImageProperty],
    'icon-anchor': 'bottom',
    'icon-offset': [0, 5],
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-field': textField,
    'text-anchor': 'top',
    'text-justify': 'center',
    'text-offset': [0, 0.5],
    'text-optional': true,
    'text-size': 14,
    'text-padding': 8,
    'text-ignore-placement': true,
    'text-allow-overlap': true,
    'text-max-angle': 45,
    'icon-size': 0.9,
  },
  paint: {
    'text-color': 'white',
    'text-opacity': 1,
  },
});

// Rating badge layer configuration factory - with star offset
const createRatingBadgeLayerConfig = (
  id: string,
  filter: mapboxgl.SymbolLayer['filter'],
  withStar = true
): LayerProps => ({
  id,
  type: 'symbol',
  filter,
  layout: {
    'symbol-z-order': 'viewport-y',
    'text-field': FILTER_GET_FORMATTED_RATING,
    'text-font': COMMON_TEXT_FONT,
    'text-size': 12,
    'text-anchor': 'center',
    'text-justify': 'center',
    'text-offset': withStar ? [-0.5, -1.05] : [0, -1.05],
    'text-allow-overlap': true,
    'text-ignore-placement': true,
  },
  paint: {
    'text-color': discoverColors.blue.DEFAULT,
    'text-halo-color': '#fff',
    'text-halo-width': 1,
  },
});

// Star icon layer configuration factory
const createStarIconLayerConfig = (
  id: string,
  filter: mapboxgl.SymbolLayer['filter']
): LayerProps => ({
  id,
  type: 'symbol',
  filter,
  layout: {
    'symbol-z-order': 'viewport-y',
    'icon-image': 'gold-star',
    'icon-size': 0.5,
    'icon-anchor': 'center',
    'icon-offset': [18, -25.5],
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  },
});

// Badge layer configuration factory (for white badges)
const createBadgeLayerConfig = (
  id: string,
  filter: mapboxgl.SymbolLayer['filter']
): LayerProps => ({
  id,
  type: 'symbol',
  filter,
  layout: {
    'symbol-z-order': 'viewport-y',
    'icon-image': 'badge-white',
    'icon-anchor': 'center',
    'icon-offset': [0, -16],
    'icon-size': 0.8,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  },
});

// Image layer configuration factory
const createImageLayerConfig = (
  id: string,
  filter: mapboxgl.SymbolLayer['filter']
): LayerProps => ({
  id,
  type: 'symbol',
  filter,
  layout: {
    'symbol-z-order': 'viewport-y',
    'icon-image': [
      'case',
      FILTER_HAS_IMAGE,
      ['concat', 'restaurant-image-', ['get', 'id']],
      [
        'case',
        FILTER_OFFERS_FIRST_TABLE,
        'restaurant-image-placeholder-gold',
        'restaurant-image-placeholder-white',
      ],
    ],
    'icon-anchor': 'center',
    'icon-offset': [0, -46.5],
    'icon-size': 0.62,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  },
  paint: {
    'icon-opacity': 1,
    'icon-translate': [0, 0],
  },
});

// Name layer configuration factory
const createNameLayerConfig = (
  id: string,
  filter: mapboxgl.SymbolLayer['filter'],
  textColor = '#000'
): LayerProps => ({
  id,
  type: 'symbol',
  filter,
  layout: {
    'symbol-z-order': 'viewport-y',
    'text-field': [
      'concat',
      [
        'case',
        ['>', ['length', FILTER_GET_TITLE], MAX_NAME_LENGTH],
        ['concat', ['slice', FILTER_GET_TITLE, 0, MAX_NAME_LENGTH], '...'],
        FILTER_GET_TITLE,
      ],
    ],
    'text-anchor': 'top',
    'text-justify': 'center',
    'text-offset': [0, 0.5],
    'text-optional': true,
    'text-size': 12,
    'text-allow-overlap': false,
    'text-ignore-placement': false,
    'text-font': COMMON_TEXT_FONT,
  },
  paint: {
    'text-color': textColor,
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
});

// Create restaurant pin layers
export const restaurantPinsLayer = createPinLayerConfig(
  RESTAURANT_LAYER_ID,
  FILTER_NOT_CLUSTERS,
  'iconImage',
  ''
);

export const selectedRestaurantPinLayer = createPinLayerConfig(
  'selected-restaurant',
  FILTER_IS_SELECTED,
  'selectedIconImage',
  ''
);

// Create restaurant name layer
export const restaurantNameLayer = createNameLayerConfig(
  'restaurant-name',
  FILTER_NOT_CLUSTERS,
  discoverColors.blue.DEFAULT
);

// Create rating badge layers
export const restaurantRatingBadgeLayer = createRatingBadgeLayerConfig(
  'restaurant-rating',
  ['all', FILTER_NOT_CLUSTERS, ['!', FILTER_IS_SELECTED], FILTER_HAS_VALID_RATING],
  true
);

// Create rating badge layer for restaurants without valid ratings (New restaurants) - centered
export const restaurantNewBadgeLayer = createRatingBadgeLayerConfig(
  'restaurant-new',
  ['all', FILTER_NOT_CLUSTERS, ['!', FILTER_IS_SELECTED], ['!', FILTER_HAS_VALID_RATING]],
  false
);

export const restaurantStarLayer = createStarIconLayerConfig('restaurant-star', [
  'all',
  FILTER_NOT_CLUSTERS,
  ['!', FILTER_IS_SELECTED],
  FILTER_HAS_VALID_RATING,
]);

export const selectedRestaurantRatingBadgeLayer = createRatingBadgeLayerConfig(
  'selected-restaurant-rating',
  ['all', FILTER_IS_SELECTED, FILTER_HAS_VALID_RATING],
  true
);

// Create rating badge layer for selected restaurants without valid ratings - centered
export const selectedRestaurantNewBadgeLayer = createRatingBadgeLayerConfig(
  'selected-restaurant-new',
  ['all', FILTER_IS_SELECTED, ['!', FILTER_HAS_VALID_RATING]],
  false
);

export const selectedRestaurantStarLayer = createStarIconLayerConfig('selected-restaurant-star', [
  'all',
  FILTER_IS_SELECTED,
  FILTER_HAS_VALID_RATING,
]);

// Create badge layers for white badges
export const restaurantBadgeLayer = createBadgeLayerConfig('restaurant-badge', [
  'all',
  FILTER_NOT_CLUSTERS,
  ['!', FILTER_IS_SELECTED],
]);

export const selectedRestaurantBadgeLayer = createBadgeLayerConfig('selected-restaurant-badge', [
  'all',
  FILTER_IS_SELECTED,
]);

// Create restaurant images layer
export const restaurantImagesLayer = createImageLayerConfig('restaurant-image', [
  'all',
  FILTER_NOT_CLUSTERS,
  FILTER_HAS_IMAGE,
  ['!', FILTER_IS_SELECTED],
]);

// Create selected restaurant images layer
export const selectedRestaurantImagesLayer = createImageLayerConfig('selected-restaurant-image', [
  'all',
  FILTER_NOT_CLUSTERS,
  FILTER_HAS_IMAGE,
  FILTER_IS_SELECTED,
]);

/**
 * Setup restaurant layer event handlers
 * @param mapRef - Reference to the mapbox map instance
 * @param setSelectedRestaurantId - Callback function to set the selected restaurant ID
 */
export const setupRestaurantLayers = (
  mapRef: MapRef,
  setSelectedRestaurantId: (id: number | null) => void
): void => {
  // select restaurant on click
  mapRef.on('click', RESTAURANT_LAYER_ID, (event) => {
    const id = event.features?.[0].properties?.id;
    const selectedId = typeof id === 'number' && !Number.isNaN(id) ? id : null;

    setSelectedRestaurantId(selectedId);
  });
};
